import type { Entity, EntityDiff } from '../types'

/**
 * Decoder for Home Assistant's `subscribe_entities` compressed-state format.
 *
 * Message shape:
 *   { a?: { id: CompressedState },           // added (and the initial snapshot)
 *     c?: { id: { '+'?: Partial, '-'?: Removed } },  // changed
 *     r?: string[] }                          // removed
 *
 * CompressedState: { s: state, a: attributes, lc: last_changed(sec),
 *                    lu: last_updated(sec), c: context }
 *
 * We keep a running EntityMap so `changed` entries (which are *partial*) can be
 * merged against the previous full state. Unchanged entities keep their object
 * identity — the store relies on that for cheap selector equality.
 */

interface CompressedState {
  s?: string
  a?: Record<string, unknown>
  lc?: number
  lu?: number
  c?: unknown
}

interface ChangedEntry {
  '+'?: CompressedState
  '-'?: { a?: string[] }
}

export interface EntitiesMessage {
  a?: Record<string, CompressedState>
  c?: Record<string, ChangedEntry>
  r?: string[]
}

const secToIso = (sec: number | undefined, fallback: string): string =>
  sec == null ? fallback : new Date(sec * 1000).toISOString()

export class EntityDecoder {
  private readonly map = new Map<string, Entity>()

  reset(): void {
    this.map.clear()
  }

  /** Apply one message, returning the diff and mutating the running map. */
  apply(msg: EntitiesMessage): EntityDiff {
    const added: Record<string, Entity> = {}
    const changed: Record<string, Entity> = {}
    const removed: string[] = []

    if (msg.a) {
      for (const id in msg.a) {
        const c = msg.a[id]!
        const now = new Date().toISOString()
        const entity: Entity = {
          entity_id: id,
          state: c.s ?? 'unknown',
          attributes: c.a ?? {},
          last_changed: secToIso(c.lc, now),
          last_updated: secToIso(c.lu ?? c.lc, now),
        }
        this.map.set(id, entity)
        added[id] = entity
      }
    }

    if (msg.c) {
      for (const id in msg.c) {
        const entry = msg.c[id]!
        const prev = this.map.get(id)
        const base: Entity = prev ?? {
          entity_id: id,
          state: 'unknown',
          attributes: {},
          last_changed: new Date().toISOString(),
          last_updated: new Date().toISOString(),
        }
        const plus = entry['+']
        const minus = entry['-']

        let attributes = base.attributes
        if (plus?.a || minus?.a) {
          attributes = { ...base.attributes }
          if (plus?.a) Object.assign(attributes, plus.a)
          if (minus?.a) for (const key of minus.a) delete attributes[key]
        }

        const next: Entity = {
          entity_id: id,
          state: plus?.s ?? base.state,
          attributes,
          last_changed: plus?.lc != null ? secToIso(plus.lc, base.last_changed) : base.last_changed,
          last_updated: plus?.lu != null ? secToIso(plus.lu, base.last_updated) : base.last_updated,
        }
        this.map.set(id, next)
        changed[id] = next
      }
    }

    if (msg.r) {
      for (const id of msg.r) {
        this.map.delete(id)
        removed.push(id)
      }
    }

    return { added, changed, removed }
  }
}
